import { BugReport as BugReportIcon, Build as BuildIcon, Close as CloseIcon, ExpandMore as ExpandMoreIcon, NewReleases as NewReleasesIcon, Star as StarIcon } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { getCurrentEnvironment, useReleaseNotes } from "../hooks/useReleaseNotes";
import { ReleaseNote } from "../types/releaseNotes";

interface ReleaseNotesModalProps {
  open: boolean;
  onClose: () => void;
}

const LAST_SEEN_VERSION_KEY = "costaff_last_seen_version";

export function ReleaseNotesModal({ open, onClose }: ReleaseNotesModalProps) {
  const { releaseNotes, markAsSeen, isLoading, error } = useReleaseNotes();
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());

  // Toggle accordion expansion
  const toggleExpanded = (version: string) => {
    const newExpanded = new Set(expandedVersions);
    if (newExpanded.has(version)) {
      newExpanded.delete(version);
    } else {
      newExpanded.add(version);
    }
    setExpandedVersions(newExpanded);
  };

  // Handle close and mark as seen
  const handleClose = () => {
    markAsSeen();
    onClose();
  };

  // Render release note item
  const renderReleaseNote = (release: ReleaseNote, index: number) => {
    const isExpanded = expandedVersions.has(release.version);

    return (
      <Accordion
        key={release.version}
        expanded={isExpanded}
        onChange={() => toggleExpanded(release.version)}
        sx={{
          mb: 1,
          "&:before": {
            display: "none",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <Box display="flex" alignItems="center" width="100%">
            <Box flexGrow={1}>
              <Typography variant="h6" component="span">
                {release.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Version {release.version} • {new Date(release.date).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0 }}>
          <Box>
            {/* Features */}
            {release.features.length > 0 && (
              <Box mb={2}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <StarIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" color="primary">
                    New Features
                  </Typography>
                </Box>
                <List dense>
                  {release.features.map((feature, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemText primary={feature} primaryTypographyProps={{ variant: "body2" }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Improvements */}
            {release.improvements.length > 0 && (
              <Box mb={2}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <BuildIcon color="secondary" fontSize="small" />
                  <Typography variant="subtitle2" color="secondary">
                    Improvements
                  </Typography>
                </Box>
                <List dense>
                  {release.improvements.map((improvement, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemText primary={improvement} primaryTypographyProps={{ variant: "body2" }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Bug Fixes */}
            {release.bugFixes.length > 0 && (
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <BugReportIcon color="success" fontSize="small" />
                  <Typography variant="subtitle2" color="success.main">
                    Bug Fixes
                  </Typography>
                </Box>
                <List dense>
                  {release.bugFixes.map((bugFix, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemText primary={bugFix} primaryTypographyProps={{ variant: "body2" }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  if (isLoading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading release notes...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error">Failed to load release notes: {error}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!releaseNotes) {
    return null;
  }

  const environment = getCurrentEnvironment();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <NewReleasesIcon color="primary" />
            <Typography variant="h6">What&apos;s New</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Current version: v{releaseNotes.currentVersion} ({environment})
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box>
          {releaseNotes.releaseNotes.map((release, index) => (
            <Box key={release.version}>
              {renderReleaseNote(release, index)}
              {index < releaseNotes.releaseNotes.length - 1 && <Divider sx={{ my: 2 }} />}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
}
